import { Avatar, Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';
import AppRoutes from '@/const/routes';
import { useUser } from '@/hooks';
import useDeviceWidth from '@/hooks/useDeviceWidth';
import { CaseInquiredAbout, Category } from '@/interfaces';
import { ConversationInfoTo } from '@/interfaces/inbox';
import { caseCategoryColorBg, caseCategoryColorText, getCaseIcon } from '@/lib';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import Link from 'next/link';
import * as React from 'react';

interface ConversationHeaderProps {
	to?: ConversationInfoTo;
	from?: ConversationInfoTo;
	category?: CaseInquiredAbout;
	caseTitle: string;
	subject: string;
	date?: string;
	isYourLawyer?: boolean;
}

interface ToLabelProps {
	to?: ConversationInfoTo;
	from?: ConversationInfoTo;
	isYourLawyer?: boolean;
	date?: string;
	className?: string;
}

interface CaseLabelProps {
	caseLabel: string;
	category?: CaseInquiredAbout;
	className?: string;
}

interface QuestionProps {
	question: string;
	className?: string;
}

const MessageLabel: React.FC<ToLabelProps> = ({
	to,
	from,
	date,
	className,
}) => {
	const { user } = useUser();

	return (
		<div
			className={clsx('flex items-center justify-between gap-x-2', className)}
		>
			<div className="flex gap-x-2 items-center">
				<Typography type="regular-14" className="text-opacity-black">
					Message:
				</Typography>
				<span
					className={clsx(
						'flex items-center gap-x-2 lg:bg-grey-5 lg:px-2 py-1 rounded-lg',
						{ 'animate-pulse w-[150px]': !to }
					)}
				>
					{user?.role === 'lawyer' ? (
						<Link
							href={`${AppRoutes.LAWYERAPP_CLIENT_PROFILE}/${to?.id}`}
							target="_blank"
						>
							<Avatar
								photoUrl={to?.profile_picture || ''}
								firstname={to?.firstname}
								lastname={to?.lastname}
								size="extra-small"
							/>
						</Link>
					) : (
						<Avatar
							photoUrl={from?.profile_picture || ''}
							firstname={from?.firstname ? from?.firstname : 'Your'}
							lastname={from?.lastname ? from?.firstname : "Lawyer's"}
							size="extra-small"
						/>
					)}

					{user?.role === 'lawyer' ? (
						<Link
							href={`${AppRoutes.LAWYERAPP_CLIENT_PROFILE}/${to?.id}`}
							target="_blank"
						>
							<Typography type="regular-14" className="capitalize">
								{`${to?.firstname || ''} ${to?.lastname || ''}`}
							</Typography>
						</Link>
					) : (
						<Typography type="regular-14" className="capitalize">
							{from
								? `${from?.firstname || ''} ${from?.lastname || ''}`
								: "Your Lawyer's"}
						</Typography>
					)}
				</span>
			</div>
			{date && (
				<Typography type="regular-14" className="text-opacity-black">
					{format(parseISO(date), 'd, MMMM yyyy')}
				</Typography>
			)}
		</div>
	);
};

const QuestionLabel: React.FC<QuestionProps> = ({
	question = '',
	className,
}) => {
	return (
		<div className={clsx('flex gap-x-2 items-center', className)}>
			<Typography type="regular-14" className="text-opacity-black">
				Subject:
			</Typography>
			<span
				className={clsx(
					'flex items-center gap-x-2 bg-grey-5 px-2 py-1 rounded-lg',
					{ 'animate-pulse w-[150px]': !question }
				)}
			>
				<span className="w-4 h-4">
					<Icon
						src={Icons.sms}
						className="text-[#C0D653]"
						width={16}
						height={16}
					/>
				</span>
				<Typography type="regular-14">{question}</Typography>
			</span>
		</div>
	);
};

export const CaseLabel: React.FC<CaseLabelProps> = ({
	caseLabel,
	category,
	className,
}) => {
	return (
		<div className={clsx('flex gap-x-2 items-center', className)}>
			<Typography type="regular-14" className="text-opacity-black">
				Case:
			</Typography>
			{category && (
				<span
					className={clsx(
						'flex items-center gap-x-2 px-2 py-1 rounded-xl',
						{ 'animate-pulse w-[150px] bg-grey-5': !caseLabel },

						caseCategoryColorBg(category as Category)
					)}
				>
					{getCaseIcon(category as Category)}
					<Typography
						type="custom-p"
						className={clsx(
							'text-left text-xs lg:text-sm',
							caseCategoryColorText(category as Category)
						)}
					>
						{caseLabel}
					</Typography>
				</span>
			)}
		</div>
	);
};

export const ConversationHeader: React.FC<ConversationHeaderProps> = ({
	to,
	from,
	caseTitle,
	category,
	subject,
	date,
	isYourLawyer,
}) => {
	const width = useDeviceWidth();
	const isMobile = width < 768;

	return (
		<div
			className={clsx(
				'flex flex-col gap-y-2',
				date ? 'max-w-full' : ' max-w-[568px]'
			)}
		>
			<MessageLabel
				to={to}
				from={from}
				date={date}
				isYourLawyer={isYourLawyer}
				className={clsx({
					'pb-2 border-b border-[#0D061614]': !date || isMobile,
				})}
			/>
			{caseTitle &&
				!caseTitle.startsWith('General') &&
				!caseTitle.startsWith('Account') && (
					<CaseLabel
						caseLabel={caseTitle}
						category={category}
						className={clsx({ 'pb-2 border-b border-[#0D061614]': !date })}
					/>
				)}
			<QuestionLabel
				question={subject}
				className={clsx('border-b border-[#0D061614]', date ? 'pb-4' : 'pb-2')}
			/>
		</div>
	);
};
